/**
 * Tự phục hồi khi tải chunk động thất bại.
 *
 * App chia code theo chunk có hash trong tên và nạp bằng `React.lazy(() => import(...))` (xem
 * common/SchemaForm/SchemaElement.tsx). Có hai tình huống làm một chunk không tải được, cả hai đều
 * KHÔNG phải lỗi logic của app:
 *
 * 1. Đang deploy dở: thư mục /assets bị thay trong lúc người dùng đang dùng, chunk cũ vừa bị xoá
 *    và chunk mới chưa có.
 * 2. Trình duyệt còn giữ index.html của bản build trước (nginx hiện không gửi Cache-Control cho
 *    index.html nên trình duyệt tự suy diễn thời gian cache) → HTML cũ trỏ tới tên chunk đã bị xoá.
 *
 * Khi đó Vite phát sự kiện `vite:preloadError`; mặc định lỗi được ném ra và người dùng thấy màn
 * "Unexpected Application Error", phải tự F5 mới dùng lại được. Ở đây tải lại trang giúp họ — trang
 * mới lấy index.html hiện hành nên trỏ đúng chunk đang có.
 *
 * Chống lặp vô hạn: chỉ tự tải lại MỘT lần cho mỗi lần "app sống ổn định". Cờ đặt trong
 * sessionStorage trước khi reload và chỉ được xoá sau khi app chạy ổn một lúc (xem
 * `markChunkReloadRecovered`). Nếu tải lại mà vẫn lỗi ngay thì cờ còn đó, lần sau để lỗi hiện ra
 * thay vì reload tiếp.
 */
const RELOAD_FLAG = 'chunk-reload-attempted';
/** Sau khoảng này coi như app đã chạy ổn định, cho phép lần tự phục hồi tiếp theo. */
const RECOVERED_AFTER_MS = 30_000;

/** sessionStorage có thể bị chặn (chế độ riêng tư, cấu hình bảo mật) — không để nó làm app chết. */
const safeSession = {
  get(key: string): string | null {
    try {
      return window.sessionStorage.getItem(key);
    } catch {
      return null;
    }
  },
  set(key: string, value: string): boolean {
    try {
      window.sessionStorage.setItem(key, value);
      return true;
    } catch {
      return false;
    }
  },
  remove(key: string) {
    try {
      window.sessionStorage.removeItem(key);
    } catch {
      /* bỏ qua */
    }
  },
};

/** Có nên tự tải lại trang cho lần lỗi chunk này không. Tách riêng để unit test được. */
export const shouldReloadOnChunkError = (alreadyAttempted: boolean): boolean => !alreadyAttempted;

export const registerChunkReload = () => {
  window.addEventListener('vite:preloadError', (event) => {
    const alreadyAttempted = safeSession.get(RELOAD_FLAG) === '1';
    if (!shouldReloadOnChunkError(alreadyAttempted)) return; // để lỗi hiện ra, KHÔNG reload tiếp

    // Chỉ chặn lỗi mặc định khi thật sự sắp reload; nếu không thì để app báo lỗi như thường.
    event.preventDefault();
    safeSession.set(RELOAD_FLAG, '1');
    window.location.reload();
  });
};

/** Gọi sau khi app đã render: chạy êm một lúc thì mở lại quyền tự phục hồi cho lần sau. */
export const markChunkReloadRecovered = () => {
  window.setTimeout(() => safeSession.remove(RELOAD_FLAG), RECOVERED_AFTER_MS);
};
